import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

// Limited palette: color name -> { sidebar, btn, btnHover, accent, accentLight, ring }
const THEME_PALETTE = {
  orange:  { sidebar: '#1e293b', btn: '#f97316', btnHover: '#ea580c', accent: '#f97316', accentLight: '#fff7ed', ring: '#fdba74', badge: 'bg-orange-100 text-orange-800' },
  blue:    { sidebar: '#0f172a', btn: '#3b82f6', btnHover: '#2563eb', accent: '#3b82f6', accentLight: '#eff6ff', ring: '#93c5fd', badge: 'bg-blue-100 text-blue-800' },
  green:   { sidebar: '#0f1f17', btn: '#22c55e', btnHover: '#16a34a', accent: '#22c55e', accentLight: '#f0fdf4', ring: '#86efac', badge: 'bg-green-100 text-green-800' },
  red:     { sidebar: '#1c1017', btn: '#ef4444', btnHover: '#dc2626', accent: '#ef4444', accentLight: '#fef2f2', ring: '#fca5a5', badge: 'bg-red-100 text-red-800' },
  violet:  { sidebar: '#1a0f2e', btn: '#8b5cf6', btnHover: '#7c3aed', accent: '#8b5cf6', accentLight: '#f5f3ff', ring: '#c4b5fd', badge: 'bg-violet-100 text-violet-800' },
  teal:    { sidebar: '#0d1f22', btn: '#14b8a6', btnHover: '#0d9488', accent: '#14b8a6', accentLight: '#f0fdfa', ring: '#5eead4', badge: 'bg-teal-100 text-teal-800' },
  amber:   { sidebar: '#1c1a0f', btn: '#f59e0b', btnHover: '#d97706', accent: '#f59e0b', accentLight: '#fffbeb', ring: '#fcd34d', badge: 'bg-amber-100 text-amber-800' },
  slate:   { sidebar: '#1e293b', btn: '#64748b', btnHover: '#475569', accent: '#64748b', accentLight: '#f8fafc', ring: '#94a3b8', badge: 'bg-slate-100 text-slate-800' },
};

function applyThemeVars(colorName) {
  const t = THEME_PALETTE[colorName] || THEME_PALETTE.orange;
  const root = document.documentElement;
  root.style.setProperty('--theme-sidebar', t.sidebar);
  root.style.setProperty('--theme-btn', t.btn);
  root.style.setProperty('--theme-btn-hover', t.btnHover);
  root.style.setProperty('--theme-accent', t.accent);
  root.style.setProperty('--theme-accent-light', t.accentLight);
  root.style.setProperty('--theme-ring', t.ring);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEmpresaId, setActiveEmpresaId] = useState(null);
  const [activeEmpresaNombre, setActiveEmpresaNombre] = useState(null);
  const [themeColor, setThemeColor] = useState('orange');
  const [visibleModules, setVisibleModules] = useState(null); // null = all visible

  // Axios interceptor: attach token + empresa context header
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('veloflux_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const eid = localStorage.getItem('veloflux_active_empresa');
      if (eid) {
        config.headers['X-Empresa-Id'] = eid;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  const fetchTheme = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/empresas/theme`);
      const color = res.data.color_primario || 'orange';
      setThemeColor(color);
      applyThemeVars(color);
    } catch {
      applyThemeVars('orange');
    }
  }, []);

  const fetchVisibleModules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/empresas/my-modules`);
      setVisibleModules(res.data.visible_modules);
    } catch {
      setVisibleModules(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('veloflux_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/api/auth/me`);
      const u = response.data;
      setUser(u);

      const stored = localStorage.getItem('veloflux_active_empresa');
      if (u.role === 'super_admin' && stored) {
        setActiveEmpresaId(stored);
        setActiveEmpresaNombre(localStorage.getItem('veloflux_active_empresa_nombre') || u.empresa_nombre);
      } else {
        setActiveEmpresaId(u.empresa_id);
        setActiveEmpresaNombre(u.empresa_nombre);
      }

      await fetchTheme();
      await fetchVisibleModules();
    } catch {
      localStorage.removeItem('veloflux_token');
      localStorage.removeItem('veloflux_active_empresa');
      localStorage.removeItem('veloflux_active_empresa_nombre');
    } finally {
      setLoading(false);
    }
  }, [fetchTheme, fetchVisibleModules]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    localStorage.removeItem('veloflux_token');
    localStorage.removeItem('veloflux_active_empresa');
    localStorage.removeItem('veloflux_active_empresa_nombre');

    const response = await axios.post(`${API}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('veloflux_token', access_token);

    if (userData.empresa_id) {
      localStorage.setItem('veloflux_active_empresa', userData.empresa_id);
      localStorage.setItem('veloflux_active_empresa_nombre', userData.empresa_nombre || '');
    }

    const freshRes = await axios.get(`${API}/api/auth/me`);
    const freshUser = freshRes.data;
    setUser(freshUser);
    setActiveEmpresaId(freshUser.empresa_id);
    setActiveEmpresaNombre(freshUser.empresa_nombre);
    console.log('[Auth] Login successful. Role:', freshUser.role, '| User:', freshUser.email);
    console.log(`[Auth] environment = production | user role = ${freshUser.role.toUpperCase()}`);

    await fetchTheme();
    await fetchVisibleModules();
    return freshUser;
  };

  const logout = () => {
    localStorage.removeItem('veloflux_token');
    localStorage.removeItem('veloflux_active_empresa');
    localStorage.removeItem('veloflux_active_empresa_nombre');
    setUser(null);
    setActiveEmpresaId(null);
    setActiveEmpresaNombre(null);
    applyThemeVars('orange');
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/api/auth/register`, {
      email, password, name, role: 'mechanic',
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('veloflux_token', access_token);
    setUser(userData);
    return userData;
  };

  const switchEmpresa = (empresaId, empresaNombre) => {
    setActiveEmpresaId(empresaId);
    setActiveEmpresaNombre(empresaNombre);
    localStorage.setItem('veloflux_active_empresa', empresaId);
    localStorage.setItem('veloflux_active_empresa_nombre', empresaNombre);
    window.dispatchEvent(new Event('empresa-switched'));
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'super_admin') return true;
    return (user.permissions || []).includes(perm);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isPending = user?.status === 'pending' || (!user?.empresa_id && user?.role !== 'super_admin');

  // Module visibility check
  const isModuleVisible = (moduleKey) => {
    if (isSuperAdmin) return true; // Super admin always sees all
    if (!visibleModules) return true; // null means all visible
    return visibleModules.includes(moduleKey);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user, login, logout, register,
      isAdmin, isSuperAdmin, isPending, hasPermission,
      activeEmpresaId, activeEmpresaNombre, switchEmpresa,
      themeColor, THEME_PALETTE,
      visibleModules, isModuleVisible, fetchVisibleModules,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
